import { useEffect, useState, useMemo } from "react";
import Tabs from "../branchDepartment/components/tabs";
import { getOrgFormsThunk, getOrgFormFieldsThunk } from "./formsThunk";
import { useDispatch, useSelector } from "react-redux";
import DataTableWithCheckbox from "../../components/dataTableWithSearch/DataTableWithCheckBox";
import SnackbarMessage from "../../components/snackBar/snackBar";
import { Box, Stack, Avatar, Typography, Button } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FilterMultiSelect from "../../components/selections/filterMultiSelect";
import DynamicFormOutlinedIcon from "@mui/icons-material/DynamicFormOutlined";
import { QR } from "../qrCode/components/qrUi";
import ViewFormModal from "./viewFormModal";

// Colour + initials per organization (display only)
const ORG_COLORS = ["#198754", "#2563eb", "#7c3aed", "#0891b2", "#d97706", "#db2777", "#4f46e5"];
const orgColor = (name = "") => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return ORG_COLORS[h % ORG_COLORS.length];
};
const orgInitials = (name = "") =>
    name.trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join("") || "?";

export default function FormList() {
    const dispatch = useDispatch();
    const { organizatiomForms = [], status, error } = useSelector((state) => state.organizatiomForm);
    console.log("organizatiomForms",organizatiomForms);
    
    useEffect(() => {
        dispatch(getOrgFormsThunk());
        dispatch(getOrgFormFieldsThunk());
    }, [dispatch]);

    const [selectOrgValues, setSelectOrgOptions] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [rowData, setRowData] = useState([]);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const columns = useMemo(
        () => [
            { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
            {
                accessorKey: "organization_name", header: "Organization Name", Cell: ({ row }) => {
                    const c = orgColor(row.original.organization_name || "");
                    return (
                        <Stack direction="row" alignItems="center" spacing={1.1} sx={{ minWidth: 0 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 11.5, fontWeight: 800, color: "#fff", background: `linear-gradient(135deg, ${c}, ${c}b3)` }}>
                                {orgInitials(row.original.organization_name || "")}
                            </Avatar>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 700, color: "#0f172a" }}>
                                {row.original.organization_name}
                            </Typography>
                        </Stack>
                    );
                }
            },
            {
                accessorKey: "form_name", header: "Form Name", Cell: ({ row }) => row.original.form_name ? (
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1, py: 0.35, borderRadius: 5, fontSize: 12.5, fontWeight: 700, color: QR.purple, bgcolor: `${QR.purple}10`, border: `1px solid ${QR.purple}26` }}>
                        <DynamicFormOutlinedIcon sx={{ fontSize: 15 }} />
                        {row.original.form_name}
                    </Box>
                ) : <Typography variant="body2" color="#cbd5e1">-</Typography>
            },
            {
                id: "view_form",
                header: "Form",
                enableGlobalFilter: false,
                Cell: ({ row }) => (
                    <Button
                        size="small"
                        startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "17px !important" }} />}
                        onClick={() => {
                            setOpenModal(true);
                            setRowData([row.original]);
                        }}
                        sx={{
                            height: 30,
                            px: 1.25,
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: 12.5,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            color: QR.blue,
                            bgcolor: `${QR.blue}0f`,
                            border: `1px solid ${QR.blue}33`,
                            "&:hover": { bgcolor: QR.blue, color: "#fff", borderColor: QR.blue },
                        }}
                    >
                        View Form
                    </Button>
                ),
            },
        ], []
    );

    useEffect(() => {
        if (status === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [status, error]);


    // Filter options for branch/department
    const orgFormOptions = organizatiomForms
        .filter(item => item.organization_id != null && item.organization_name != null)
        .map((item) => ({ value: item.organization_id, label: item.organization_name }))
        .filter((obj, index, self) => index === self.findIndex((o) => o.value === obj.value));

    useEffect(() => {
        setSelectOrgOptions(orgFormOptions);
    }, [organizatiomForms]);

    const handleMultiSelectChange = (_name, selectedValues) => {
        setSelectOrgOptions(selectedValues);
    };

    const renderForms = () => {
        return (
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2 }}>
                <Box sx={{ width: { xs: "100%", sm: 260 } }}>
                    <FilterMultiSelect
                        name={selectOrgValues}
                        label="Organization"
                        options={orgFormOptions || []}
                        value={selectOrgValues || []}
                        onChange={handleMultiSelectChange}
                    />
                </Box>
            </Box>
        )
    }

    return (
        <>
            <Tabs>
                <DataTableWithCheckbox
                    enableRowSelection={false}
                    columns={columns}
                    data={organizatiomForms.filter((item) => selectOrgValues.map((item) => +item.value).includes(+item.organization_id)) || []}
                    status={status}
                    renderFilters={renderForms()}
                    searchFirst
                    emptyMessage="No organization forms for this selection."
                />
            </Tabs>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />

            <ViewFormModal show={openModal} handleClose={() => { setOpenModal(false); setRowData([]) }} formData={rowData} />
        </>
    );
};